import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { COUNTRY_CODES } from "@/assets/countryCodes";
import { cloudinaryUpload } from "@/services/middleware/uploadImage";
import { editUserInfo, logout } from "@/services/slices/auth.slice";
import { getStudentProfileAPI } from "@/services/api/user/getStudentProfile";
import {
  EditProfileAPI,
  sendProfilePhoneVerificationCodeAPI,
  verifyProfilePhoneCodeAPI,
} from "@/services/api/user/editProfile";
import { ChangePasswordAPI } from "@/services/api/user/changePassword";
import {
  formatPhoneNumber,
  isSamePhoneNumber,
  splitPhoneNumber,
  validatePhoneNumber,
} from "@/utils/phone";
import {
  GetPendingSocietiesAPI,
  GetUserSocietiesAPI,
  type UserSociety,
} from "@/services/api/society/getUserSocieties";
import {
  CountFollowingAPI,
  GetFollowingAPI,
} from "@/services/api/user/following";
import { CountSubscriptedAPI } from "@/services/api/user/countSubscripted";

export interface FollowingSociety {
  id?: string;
  name?: string;
  name_en?: string;
  name_zh_hant?: string;
  icon?: string | null;
  society_icon?: string | null;
  university?: string | null;
}

const PASSWORD_MIN_LENGTH = 8;

const PHONE_STATUS = {
  PHONE_EXISTED: -20012,
  INVALID_PHONE: -20013,
  SMS_NOT_CONFIGURED: -20014,
  CODE_EXPIRED: -20015,
  CODE_INVALID: -20016,
  TOO_MANY_ATTEMPTS: -20017,
  SMS_SEND_FAILED: -20018,
  CODE_ALREADY_USED: -20023,
  PROFILE_PHONE_NOT_VERIFIED: -20025,
};

const describePhoneStatus = (status?: number | string): string | undefined => {
  switch (status) {
    case PHONE_STATUS.PHONE_EXISTED:
      return i18n.t("signup.phoneExists");
    case PHONE_STATUS.INVALID_PHONE:
      return i18n.t("Invalid Phone Number");
    case PHONE_STATUS.SMS_NOT_CONFIGURED:
      return i18n.t("SMS service not configured");
    case PHONE_STATUS.SMS_SEND_FAILED:
      return i18n.t("signup.smsSendFailed");
    case PHONE_STATUS.CODE_EXPIRED:
      return i18n.t("Verification code expired, please resend");
    case PHONE_STATUS.CODE_INVALID:
      return i18n.t("Invalid verification code");
    case PHONE_STATUS.TOO_MANY_ATTEMPTS:
      return i18n.t("Too many attempts, please resend");
    case PHONE_STATUS.CODE_ALREADY_USED:
      return i18n.t("Verification code already used, please resend");
    case PHONE_STATUS.PROFILE_PHONE_NOT_VERIFIED:
      return i18n.t("Phone verification required");
    default:
      return undefined;
  }
};

/**
 * Web port of the RN account / setting / edit-profile / change-password hooks.
 * Owns the student profile hydration (the login payload omits the student card
 * fields), the societies and following lists, and the two profile forms.
 */
export function useStudentAccount() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const student = useSelector((state: any) => state.auth?.student);

  // Mirrored in a ref so the load effect and the form handlers stay stable and
  // do not re-fire when the profile in Redux is replaced.
  const studentRef = useRef(student);
  studentRef.current = student;

  const [societies, setSocieties] = useState<UserSociety[]>([]);
  const [pendingSocieties, setPendingSocieties] = useState<UserSociety[]>([]);
  const [followings, setFollowings] = useState<FollowingSociety[]>([]);
  const [followingCount, setFollowingCount] = useState<number | null>(null);
  const [subscribedCount, setSubscribedCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [editVisible, setEditVisible] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editRegionCode, setEditRegionCode] = useState("+852");
  const [editPhone, setEditPhone] = useState("");
  const [editOriginalPhone, setEditOriginalPhone] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editInvalid, setEditInvalid] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // New-phone SMS verification state. `pendingPhone` is frozen at send time so
  // editing the form afterwards cannot verify one number and save another.
  const [editPhoneVerifyVisible, setEditPhoneVerifyVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingRegionCode, setPendingRegionCode] = useState("+852");
  const [editPhoneCode, setEditPhoneCode] = useState("");
  const [editPhoneError, setEditPhoneError] = useState("");
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [verifyingPhoneCode, setVerifyingPhoneCode] = useState(false);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [logoutVisible, setLogoutVisible] = useState(false);

  const showAlert = useCallback((text: string) => {
    setAlertText(text);
    setAlertVisible(true);
  }, []);

  const hideAlert = useCallback(() => setAlertVisible(false), []);

  // The login payload only carries the `student_profiles` columns it reads, so
  // the student card / HKID numbers are hydrated from the profile endpoint.
  // That record carries its own `id`, which must not overwrite the account id
  // every API call sends.
  const hydrateProfile = useCallback(async () => {
    const resp = await getStudentProfileAPI();
    if (resp?.status !== 0 || !resp?.result) return;
    const { id, user, ...profile } = resp.result;
    dispatch(
      editUserInfo({
        userType: "student",
        user: { ...studentRef.current, ...profile },
      }),
    );
  }, [dispatch]);

  const fetchAccountData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      hydrateProfile(),
      GetUserSocietiesAPI().then(setSocieties),
      GetPendingSocietiesAPI().then(setPendingSocieties),
      GetFollowingAPI(1).then((resp) => {
        if (resp?.status === 0 && Array.isArray(resp.result)) {
          setFollowings(resp.result as FollowingSociety[]);
        }
      }),
      CountFollowingAPI().then((resp) => {
        if (resp?.status === 0 && typeof resp.result === "number") {
          setFollowingCount(resp.result);
        }
      }),
      CountSubscriptedAPI().then((resp) => {
        if (resp?.status === 0 && typeof resp.result === "number") {
          setSubscribedCount(resp.result);
        }
      }),
    ]);
    setLoading(false);
  }, [hydrateProfile]);

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  const openEditProfile = useCallback(() => {
    const current = studentRef.current;
    const storedPhone = String(
      current?.phone_num ?? current?.contact_number ?? "",
    );
    const parsed = splitPhoneNumber(storedPhone, COUNTRY_CODES);
    setEditUsername(current?.username ?? "");
    setEditRegionCode(parsed.regionCode);
    setEditPhone(parsed.localNumber);
    setEditOriginalPhone(storedPhone);
    setEditIcon("");
    setEditInvalid(false);
    setEditPhoneVerifyVisible(false);
    setPendingPhone("");
    setPendingRegionCode(parsed.regionCode);
    setEditPhoneCode("");
    setEditPhoneError("");
    setEditVisible(true);
  }, []);

  const pickImage = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setEditIcon(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const commitProfile = async (fullPhone: string) => {
    setSavingProfile(true);
    const profileIcon = editIcon
      ? await cloudinaryUpload(editIcon)
      : studentRef.current?.profile_icon;
    const resp = await EditProfileAPI({
      username: editUsername,
      phone_num: fullPhone,
      profile_icon: profileIcon || undefined,
    });
    setSavingProfile(false);

    if (resp?.success === false) {
      return showAlert(i18n.t("Network error, please try again"));
    }
    if (resp?.status !== 0) {
      return showAlert(
        describePhoneStatus(resp?.status) || i18n.t("Can`t Edit Profile"),
      );
    }

    dispatch(
      editUserInfo({
        userType: "student",
        user: {
          ...studentRef.current,
          ...(resp?.result ?? {}),
          username: editUsername,
          phone_num: fullPhone,
          profile_icon: profileIcon || studentRef.current?.profile_icon || "",
        },
      }),
    );
    setEditVisible(false);
    setEditPhoneVerifyVisible(false);
    showAlert(i18n.t("Profile updated successfully!"));
  };

  const resendProfilePhoneCode = async () => {
    if (!pendingPhone || sendingPhoneCode) return;
    setSendingPhoneCode(true);
    setEditPhoneError("");
    const resp = await sendProfilePhoneVerificationCodeAPI(
      pendingPhone,
      pendingRegionCode,
    );
    setSendingPhoneCode(false);
    if (resp?.status === 0) return;
    setEditPhoneError(
      describePhoneStatus(resp?.status) ||
        i18n.t("Network error, please try again"),
    );
  };

  const saveProfile = async () => {
    setEditInvalid(true);
    if (!editUsername || !editPhone) return;
    const phoneIssue = validatePhoneNumber(editRegionCode, editPhone);
    if (phoneIssue) {
      return showAlert(i18n.t(phoneIssue));
    }

    const fullPhone = formatPhoneNumber(editRegionCode, editPhone);
    if (isSamePhoneNumber(editOriginalPhone, fullPhone, COUNTRY_CODES)) {
      return commitProfile(fullPhone);
    }

    // The number changed: prove the new number before saving anything.
    setPendingPhone(fullPhone);
    setPendingRegionCode(editRegionCode);
    setEditPhoneCode("");
    setEditPhoneError("");
    setSavingProfile(true);
    const resp = await sendProfilePhoneVerificationCodeAPI(
      fullPhone,
      editRegionCode,
    );
    setSavingProfile(false);
    if (resp?.status !== 0) {
      return showAlert(
        describePhoneStatus(resp?.status) ||
          i18n.t("Network error, please try again"),
      );
    }
    setEditPhoneVerifyVisible(true);
  };

  const verifyPhoneAndSave = async () => {
    if (!editPhoneCode.trim()) {
      return setEditPhoneError(i18n.t("Please enter the SMS code"));
    }
    if (!pendingPhone || verifyingPhoneCode) return;
    setVerifyingPhoneCode(true);
    setEditPhoneError("");
    const resp = await verifyProfilePhoneCodeAPI(
      pendingPhone,
      pendingRegionCode,
      editPhoneCode.trim(),
    );
    setVerifyingPhoneCode(false);
    if (resp?.status === 0 && resp?.result?.verified) {
      setEditPhoneVerifyVisible(false);
      return commitProfile(pendingPhone);
    }
    setEditPhoneError(
      describePhoneStatus(resp?.status) ||
        i18n.t("Invalid verification code"),
    );
  };

  const openChangePassword = useCallback(() => {
    setOldPassword("");
    setNewPassword("");
    setPasswordInvalid(false);
    setPasswordVisible(true);
  }, []);

  const savePassword = async () => {
    setPasswordInvalid(true);
    if (!oldPassword || !newPassword) return;
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      return showAlert(i18n.t("Invalid New Password"));
    }

    setSavingPassword(true);
    const resp = await ChangePasswordAPI(newPassword);
    setSavingPassword(false);

    if (resp?.status !== 0) {
      return showAlert(i18n.t("Can`t Change Password"));
    }

    setPasswordVisible(false);
    showAlert(i18n.t("Password changed successfully!"));
  };

  const handleLogout = () => {
    setLogoutVisible(false);
    dispatch(logout());
    navigate("/login");
  };

  return {
    student,
    societies,
    pendingSocieties,
    followings,
    followingCount,
    subscribedCount,
    loading,
    editVisible,
    editUsername,
    editRegionCode,
    editPhone,
    editIcon,
    editInvalid,
    savingProfile,
    editPhoneVerifyVisible,
    pendingPhone,
    editPhoneCode,
    editPhoneError,
    sendingPhoneCode,
    verifyingPhoneCode,
    passwordVisible,
    oldPassword,
    newPassword,
    passwordInvalid,
    savingPassword,
    alertVisible,
    alertText,
    logoutVisible,
    setEditUsername,
    setEditRegionCode,
    setEditPhone,
    setEditPhoneCode,
    setEditPhoneError,
    setEditInvalid,
    setEditVisible,
    setEditPhoneVerifyVisible,
    setPasswordVisible,
    setOldPassword,
    setNewPassword,
    setPasswordInvalid,
    setLogoutVisible,
    openEditProfile,
    pickImage,
    saveProfile,
    resendProfilePhoneCode,
    verifyPhoneAndSave,
    openChangePassword,
    savePassword,
    hideAlert,
    handleLogout,
  };
}
